import React, { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import { useNavigate, useParams } from 'react-router-dom';
import { utils } from '../../../utils';
import { usePermissions } from '../../../hooks';
import { apiService } from '../../../services/api';
import { Config } from '../../../services/config';
import { SystemModule } from '../../../types/permissions';
import IntegracaoMarketplace from '../IntegracaoMarketplace';
import {
  buscarCliente,
  testarRegex,
  criarModeloEtiqueta,
  atualizarModeloEtiqueta,
  deletarModeloEtiqueta,
  listarAreasAtendidas,
  salvarAreasAtendidas,
  Cliente,
  EtiquetaConfig,
  EtiquetaModelo,
  AreaAtendida
} from '../../../services/clientes';
import { listarAreas } from '../../../services/montagemPalete';
import type { AreaEntrega } from '../../../types/montagemPalete';
import ViewLayout from '../../../components/Common/ViewLayout';

// =============================================
// CONFIGURADOR VISUAL DE ETIQUETAS
// =============================================

type ColunaTipo = 'ignorar' | 'cnpj' | 'area' | 'pedido' | 'volume_atual' | 'total_volumes' | 'personalizado';

interface ColunaMapeamento {
  valor: string;
  tipo: ColunaTipo;
  nomePersonalizado: string;
}

const TIPO_LABELS: Record<Exclude<ColunaTipo, 'ignorar'>, string> = {
  cnpj: 'CNPJ Destinatário',
  area: 'Cód. Área de Entrega',
  pedido: 'N. do Pedido',
  volume_atual: 'Volume Atual',
  total_volumes: 'Total de Volumes',
  personalizado: 'Personalizado'
};

const TIPO_CORES: Record<ColunaTipo, { bg: string; border: string; text: string }> = {
  ignorar: { bg: '#f1f5f9', border: '#e2e8f0', text: '#94a3b8' },
  cnpj: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  area: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  pedido: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  volume_atual: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  total_volumes: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  personalizado: { bg: '#f0fdf4', border: '#86efac', text: '#166534' }
};

const TIPO_ICONS: Record<ColunaTipo, string> = {
  ignorar: 'fas fa-minus',
  cnpj: 'fas fa-building',
  area: 'fas fa-map-marker-alt',
  pedido: 'fas fa-hashtag',
  volume_atual: 'fas fa-sort-numeric-up',
  total_volumes: 'fas fa-layer-group',
  personalizado: 'fas fa-tag'
};

const SEPARADORES_COMUNS = [
  { valor: '-', label: 'Hífen (-)' },
  { valor: '/', label: 'Barra (/)' },
  { valor: '|', label: 'Pipe (|)' },
  { valor: ';', label: 'Ponto-vírgula (;)' },
  { valor: ',', label: 'Vírgula (,)' },
  { valor: '.', label: 'Ponto (.)' },
];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function escapeCharClass(s: string): string {
  return s.replace(/[\]\\^]/g, '\\$&').replace(/-/g, '\\-');
}

function generateRegexForColumn(separator: string, columnIndex: number): string {
  const esc = escapeRegex(separator);
  const cls = escapeCharClass(separator);
  if (columnIndex === 0) {
    return `^([^${cls}]+)`;
  }
  return `^(?:[^${cls}]+${esc}){${columnIndex}}([^${cls}]+)`;
}

function autoDetectSeparator(barcode: string): string {
  const candidates = ['-', '/', '|', ';', ',', '.', ' '];
  let bestSep = '-';
  let bestCount = 0;
  for (const sep of candidates) {
    const count = barcode.split(sep).length - 1;
    if (count > bestCount && barcode.split(sep).length > 1) {
      bestCount = count;
      bestSep = sep;
    }
  }
  return bestSep;
}

const ClienteView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canUpdate } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingEtiquetas, setIsSavingEtiquetas] = useState(false);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [etiquetaModelos, setEtiquetaModelos] = useState<EtiquetaModelo[]>([]);
  const [modeloEditando, setModeloEditando] = useState<EtiquetaModelo | null>(null);
  const [modeloConfigs, setModeloConfigs] = useState<EtiquetaConfig[]>([]);
  const [modeloNome, setModeloNome] = useState('');
  const [modeloDescricao, setModeloDescricao] = useState('');
  const [modeloCampoNumeroPedido, setModeloCampoNumeroPedido] = useState('');
  const [modeloCampoVolumeAtual, setModeloCampoVolumeAtual] = useState('');
  const [modeloCampoTotalVolumes, setModeloCampoTotalVolumes] = useState('');
  const [modeloCampoCnpj, setModeloCampoCnpj] = useState('');
  const [modeloCampoCodigoSetor, setModeloCampoCodigoSetor] = useState('');
  const [showNovoModelo, setShowNovoModelo] = useState(false);
  const [testeCodigoBarras, setTesteCodigoBarras] = useState('');
  const [testeResultados, setTesteResultados] = useState<Record<number, { match: boolean; resultado: string | null; erro?: string }>>({});
  const [activeTab, setActiveTab] = useState<'dados' | 'etiquetas' | 'usuarios' | 'tokens' | 'areas'>('dados');
  // Áreas de entrega atendidas
  const [todasAreas, setTodasAreas] = useState<AreaEntrega[]>([]);
  const [areasAtendidas, setAreasAtendidas] = useState<number[]>([]);
  const [salvandoAreas, setSalvandoAreas] = useState(false);
  const [showRegexGuide, setShowRegexGuide] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Visual label configurator states
  const [modoAvancado, setModoAvancado] = useState(false);
  const [codigoBarrasExemplo, setCodigoBarrasExemplo] = useState('');
  const [separador, setSeparador] = useState('-');
  const [separadorCustom, setSeparadorCustom] = useState('');
  const [colunaMapeamentos, setColunaMapeamentos] = useState<ColunaMapeamento[]>([]);

  // Token Dashboard states
  interface DashboardTokenItem {
    id: number;
    token: string;
    descricao: string;
    ativo: boolean;
    criado_em: string;
  }
  const [dashTokens, setDashTokens] = useState<DashboardTokenItem[]>([]);
  const [dashTokensLoading, setDashTokensLoading] = useState(false);
  const [dashTokenDescricao, setDashTokenDescricao] = useState('');
  const [dashTokenCreating, setDashTokenCreating] = useState(false);
  const [dashTokenCopiedId, setDashTokenCopiedId] = useState<number | null>(null);

  const fetchDashTokens = useCallback(async () => {
    if (!id) return;
    setDashTokensLoading(true);
    try {
      const url = `${Config.API_BASE_URL}/client-dashboard/clientes/${id}/tokens`;
      const res = await apiService.request<any>(url);
      setDashTokens(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar tokens:', err);
    } finally {
      setDashTokensLoading(false);
    }
  }, [id]);

  const handleCreateDashToken = async () => {
    setDashTokenCreating(true);
    try {
      const url = `${Config.API_BASE_URL}/client-dashboard/clientes/${id}/tokens`;
      await apiService.request<any>(url, {
        method: 'POST',
        body: JSON.stringify({ descricao: dashTokenDescricao || `Token Dashboard - ${cliente?.nome_fantasia || cliente?.razao_social || ''}` })
      });
      setDashTokenDescricao('');
      await fetchDashTokens();
      utils.showNotification('Token criado com sucesso', { type: 'success' });
    } catch (err) {
      console.error('Erro ao criar token:', err);
      utils.showNotification('Erro ao criar token', { type: 'error' });
    } finally {
      setDashTokenCreating(false);
    }
  };

  const handleDeleteDashToken = async (tokenId: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este token? Todas as integrações que usam este token serão desconectadas.')) return;
    try {
      const url = `${Config.API_BASE_URL}/client-dashboard/tokens/${tokenId}`;
      await apiService.request<any>(url, { method: 'DELETE' });
      await fetchDashTokens();
      utils.showNotification('Token removido com sucesso', { type: 'success' });
    } catch (err) {
      console.error('Erro ao excluir token:', err);
      utils.showNotification('Erro ao excluir token', { type: 'error' });
    }
  };

  const copyDashToken = (tokenId: number, tokenValue: string) => {
    navigator.clipboard.writeText(tokenValue);
    setDashTokenCopiedId(tokenId);
    setTimeout(() => setDashTokenCopiedId(null), 2000);
  };

  useEffect(() => {
    const fetchCliente = async () => {
      try {
        const response = await buscarCliente(Number(id));
        if (response.success && response.data) {
          setCliente(response.data);
          setEtiquetaModelos(response.data.etiqueta_modelos || []);
        }
      } catch (error: any) {
        utils.showNotification(error.message || 'Erro ao carregar cliente', { type: 'error' });
        navigate('/clientes');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCliente();
  }, [id, navigate]);

  useEffect(() => {
    if (activeTab === 'tokens') {
      fetchDashTokens();
    }
    if (activeTab === 'areas') {
      Promise.all([
        listarAreas({ ativo: 'true' }).catch(() => ({ success: false, areas: [] as AreaEntrega[] })),
        listarAreasAtendidas(Number(id)).catch(() => ({ success: false, areas: [] as AreaAtendida[] }))
      ]).then(([areasRes, atendidasRes]) => {
        if (areasRes.success) setTodasAreas(areasRes.areas);
        if (atendidasRes.success) {
          setAreasAtendidas(atendidasRes.areas.map(a => a.area_entrega_id));
        }
      });
    }
  }, [activeTab, fetchDashTokens, id]);

  const formatCNPJ = (cnpj: string): string => {
    if (!cnpj) return '-';
    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  };

  // =============================================
  // GESTÃO DE MODELOS DE ETIQUETA
  // =============================================

  const iniciarEdicaoModelo = (modelo: EtiquetaModelo) => {
    setModeloEditando(modelo);
    setModeloNome(modelo.nome);
    setModeloDescricao(modelo.descricao || '');
    setModeloCampoNumeroPedido(modelo.campo_numero_pedido || '');
    setModeloCampoVolumeAtual(modelo.campo_volume_atual || '');
    setModeloCampoTotalVolumes(modelo.campo_total_volumes || '');
    setModeloCampoCnpj(modelo.campo_cnpj || '');
    setModeloCampoCodigoSetor(modelo.campo_codigo_setor || '');
    setModeloConfigs(modelo.configs.map(c => ({ ...c })));
    setShowNovoModelo(false);
    setTesteResultados({});
    // Modelos existentes já possuem configs com regex, então abrir no modo avançado
    setModoAvancado(true);
    setCodigoBarrasExemplo('');
    setSeparador('-');
    setSeparadorCustom('');
    setColunaMapeamentos([]);
  };

  const resetVisualState = () => {
    setModoAvancado(false);
    setCodigoBarrasExemplo('');
    setSeparador('-');
    setSeparadorCustom('');
    setColunaMapeamentos([]);
  };

  const iniciarNovoModelo = () => {
    setModeloEditando(null);
    setModeloNome('');
    setModeloDescricao('');
    setModeloCampoNumeroPedido('');
    setModeloCampoVolumeAtual('');
    setModeloCampoTotalVolumes('');
    setModeloCampoCnpj('');
    setModeloCampoCodigoSetor('');
    setModeloConfigs([]);
    setShowNovoModelo(true);
    setTesteResultados({});
    resetVisualState();
  };

  const cancelarEdicaoModelo = () => {
    setModeloEditando(null);
    setShowNovoModelo(false);
    setModeloNome('');
    setModeloDescricao('');
    setModeloCampoNumeroPedido('');
    setModeloCampoVolumeAtual('');
    setModeloCampoTotalVolumes('');
    setModeloCampoCnpj('');
    setModeloCampoCodigoSetor('');
    setModeloConfigs([]);
    setTesteResultados({});
    resetVisualState();
  };

  // Visual configurator helpers
  const splitBarcode = (barcode: string, sep: string): string[] => {
    if (!barcode || !sep) return [barcode];
    return barcode.split(sep);
  };

  const handleBarcodePaste = (value: string) => {
    setCodigoBarrasExemplo(value);
    if (value.trim()) {
      const detectedSep = autoDetectSeparator(value);
      setSeparador(detectedSep);
      const parts = splitBarcode(value, detectedSep);
      setColunaMapeamentos(parts.map(v => ({ valor: v, tipo: 'ignorar' as ColunaTipo, nomePersonalizado: '' })));
    } else {
      setColunaMapeamentos([]);
    }
  };

  const handleSeparadorChange = (newSep: string) => {
    setSeparador(newSep);
    setSeparadorCustom('');
    if (codigoBarrasExemplo.trim()) {
      const parts = splitBarcode(codigoBarrasExemplo, newSep);
      setColunaMapeamentos(prev => {
        return parts.map((v, i) => ({
          valor: v,
          tipo: prev[i]?.tipo || 'ignorar' as ColunaTipo,
          nomePersonalizado: prev[i]?.nomePersonalizado || ''
        }));
      });
    }
  };

  const updateColunaTipo = (index: number, tipo: ColunaTipo) => {
    setColunaMapeamentos(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], tipo };
      return updated;
    });
  };

  const updateColunaNome = (index: number, nome: string) => {
    setColunaMapeamentos(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], nomePersonalizado: nome };
      return updated;
    });
  };

  const buildConfigsFromVisual = (): {
    configs: EtiquetaConfig[];
    campoCnpj: string;
    campoArea: string;
    campoPedido: string;
    campoVolAtual: string;
    campoVolTotal: string;
  } => {
    const configs: EtiquetaConfig[] = [];
    let campoCnpj = '';
    let campoArea = '';
    let campoPedido = '';
    let campoVolAtual = '';
    let campoVolTotal = '';

    colunaMapeamentos.forEach((col, idx) => {
      if (col.tipo === 'ignorar') return;
      const nomeCampo = col.tipo === 'personalizado'
        ? (col.nomePersonalizado || `Campo_${idx + 1}`)
        : TIPO_LABELS[col.tipo];
      const regexPattern = generateRegexForColumn(separador, idx);
      const tipoDado = (col.tipo === 'cnpj') ? 'cnpj' : (col.tipo === 'volume_atual' || col.tipo === 'total_volumes') ? 'numero' : 'texto';

      configs.push({
        nome_campo: nomeCampo,
        regex_pattern: regexPattern,
        tipo_dado: tipoDado,
        obrigatorio: col.tipo === 'cnpj' || col.tipo === 'area',
        descricao: '',
        ordem: configs.length,
        ativo: true
      });

      if (col.tipo === 'cnpj') campoCnpj = nomeCampo;
      if (col.tipo === 'area') campoArea = nomeCampo;
      if (col.tipo === 'pedido') campoPedido = nomeCampo;
      if (col.tipo === 'volume_atual') campoVolAtual = nomeCampo;
      if (col.tipo === 'total_volumes') campoVolTotal = nomeCampo;
    });

    return { configs, campoCnpj, campoArea, campoPedido, campoVolAtual, campoVolTotal };
  };

  const addModeloConfig = () => {
    setModeloConfigs(prev => [...prev, {
      nome_campo: '',
      descricao: '',
      regex_pattern: '',
      tipo_dado: 'texto',
      obrigatorio: false,
      ordem: prev.length,
      ativo: true
    }]);
  };

  const updateModeloConfig = (index: number, field: string, value: any) => {
    setModeloConfigs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeModeloConfig = (index: number) => {
    setModeloConfigs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSalvarModelo = async () => {
    if (!modeloNome.trim()) {
      utils.showNotification('Nome do modelo é obrigatório', { type: 'error' });
      return;
    }

    // If in visual mode, build configs from visual mappings
    let configsToSave = modeloConfigs;
    let cnpjToSave = modeloCampoCnpj;
    let areaToSave = modeloCampoCodigoSetor;
    let pedidoToSave = modeloCampoNumeroPedido;
    let volAtualToSave = modeloCampoVolumeAtual;
    let volTotalToSave = modeloCampoTotalVolumes;

    if (!modoAvancado && codigoBarrasExemplo.trim()) {
      const visual = buildConfigsFromVisual();
      if (visual.configs.length === 0) {
        utils.showNotification('Mapeie pelo menos uma coluna no configurador visual', { type: 'error' });
        return;
      }
      configsToSave = visual.configs;
      cnpjToSave = visual.campoCnpj;
      areaToSave = visual.campoArea;
      pedidoToSave = visual.campoPedido;
      volAtualToSave = visual.campoVolAtual;
      volTotalToSave = visual.campoVolTotal;
    }

    // Validar configs
    for (let i = 0; i < configsToSave.length; i++) {
      const config = configsToSave[i];
      if (!config.nome_campo.trim()) {
        utils.showNotification(`Campo ${i + 1}: Nome do campo é obrigatório`, { type: 'error' });
        return;
      }
      if (!config.regex_pattern.trim()) {
        utils.showNotification(`Campo ${i + 1}: Padrão regex é obrigatório`, { type: 'error' });
        return;
      }
      try {
        new RegExp(config.regex_pattern);
      } catch {
        utils.showNotification(`Campo ${i + 1}: Padrão regex inválido`, { type: 'error' });
        return;
      }
    }

    // Validar mapeamento de volumes (se parcialmente preenchido)
    const camposVolume = [pedidoToSave, volAtualToSave, volTotalToSave].filter(Boolean);
    if (camposVolume.length > 0 && camposVolume.length < 3) {
      utils.showNotification('Rastreamento de volumes: preencha os 3 campos (N. do Pedido, Volume Atual e Total de Volumes) ou deixe todos vazios', { type: 'error' });
      return;
    }
    // Validar campos duplicados no mapeamento de volumes
    if (camposVolume.length === 3) {
      const uniqueCampos = new Set(camposVolume);
      if (uniqueCampos.size < 3) {
        utils.showNotification('Rastreamento de volumes: cada campo deve ser diferente. Não use o mesmo campo para mais de uma função.', { type: 'error' });
        return;
      }
    }

    setIsSavingEtiquetas(true);
    try {
      if (modeloEditando?.id) {
        // Atualizar existente
        const response = await atualizarModeloEtiqueta(Number(id), modeloEditando.id, {
          nome: modeloNome,
          descricao: modeloDescricao,
          campo_numero_pedido: pedidoToSave || null,
          campo_volume_atual: volAtualToSave || null,
          campo_total_volumes: volTotalToSave || null,
          campo_cnpj: cnpjToSave || null,
          campo_codigo_setor: areaToSave || null,
          configs: configsToSave
        });
        if (response.success) {
          utils.showNotification('Modelo de etiqueta atualizado com sucesso!', { type: 'success' });
          // Atualizar lista de modelos
          setEtiquetaModelos(prev => prev.map(m =>
            m.id === modeloEditando.id && response.data ? response.data : m
          ));
        }
      } else {
        // Criar novo
        const response = await criarModeloEtiqueta(Number(id), {
          nome: modeloNome,
          descricao: modeloDescricao,
          campo_numero_pedido: pedidoToSave || null,
          campo_volume_atual: volAtualToSave || null,
          campo_total_volumes: volTotalToSave || null,
          campo_cnpj: cnpjToSave || null,
          campo_codigo_setor: areaToSave || null,
          configs: configsToSave
        });
        if (response.success && response.data) {
          utils.showNotification('Modelo de etiqueta criado com sucesso!', { type: 'success' });
          setEtiquetaModelos(prev => [...prev, response.data!]);
        }
      }

      cancelarEdicaoModelo();

      // Recarregar dados completos
      const clienteResponse = await buscarCliente(Number(id));
      if (clienteResponse.success && clienteResponse.data) {
        setEtiquetaModelos(clienteResponse.data.etiqueta_modelos || []);
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar modelo', { type: 'error' });
    } finally {
      setIsSavingEtiquetas(false);
    }
  };

  const handleDeletarModelo = async (modelo: EtiquetaModelo) => {
    if (!modelo.id) return;
    if (!window.confirm(`Tem certeza que deseja excluir o modelo "${modelo.nome}"? Todos os campos configurados serão perdidos.`)) return;

    try {
      const response = await deletarModeloEtiqueta(Number(id), modelo.id);
      if (response.success) {
        utils.showNotification('Modelo excluído com sucesso!', { type: 'success' });
        setEtiquetaModelos(prev => prev.filter(m => m.id !== modelo.id));
        if (modeloEditando?.id === modelo.id) {
          cancelarEdicaoModelo();
        }
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao excluir modelo', { type: 'error' });
    }
  };

  const handleTestarTodosRegex = async () => {
    if (!testeCodigoBarras.trim()) {
      utils.showNotification('Digite um código de barras para testar', { type: 'error' });
      return;
    }

    const configsParaTestar = (!modoAvancado && codigoBarrasExemplo.trim())
      ? buildConfigsFromVisual().configs
      : modeloConfigs;
    const resultados: Record<number, { match: boolean; resultado: string | null; erro?: string }> = {};

    for (let i = 0; i < configsParaTestar.length; i++) {
      const config = configsParaTestar[i];
      if (config.regex_pattern) {
        try {
          const response = await testarRegex(config.regex_pattern, testeCodigoBarras);
          if (response.success && response.data) {
            resultados[i] = response.data;
          }
        } catch {
          resultados[i] = { match: false, resultado: null, erro: 'Erro ao testar' };
        }
      }
    }

    setTesteResultados(resultados);
    utils.showNotification('Teste concluído!', { type: 'info' });
  };

  // =============================================
  // LEITOR DE CÓDIGO DE BARRAS (CÂMERA E ARQUIVO)
  // =============================================

  // Gerenciamento da câmera para leitura de código de barras em tempo real
  useEffect(() => {
    if (!showCamera) return;

    let mounted = true;
    let activeStream: MediaStream | null = null;
    let barcodeInterval: ReturnType<typeof setInterval> | null = null;

    const onDetected = (data: string) => {
      if (!mounted) return;
      scanningRef.current = false;
      if (!modoAvancado && (showNovoModelo || modeloEditando)) {
        handleBarcodePaste(data);
      } else {
        setTesteCodigoBarras(data);
      }
      setShowCamera(false);
      utils.showNotification('Código lido com sucesso!', { type: 'success' });
    };

    const scanFrame = () => {
      if (!scanningRef.current || !mounted || !videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx || !video.videoWidth || !video.videoHeight) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code?.data) {
        onDetected(code.data);
        return;
      }

      if (scanningRef.current && mounted) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });

        activeStream = stream;

        if (!mounted || !videoRef.current) {
          stream.getTracks().forEach(t => t.stop());
          activeStream = null;
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanningRef.current = true;
        scanFrame();

        // Usar BarcodeDetector API para códigos de barras 1D (quando disponível no navegador)
        if ('BarcodeDetector' in window) {
          barcodeInterval = setInterval(async () => {
            if (!scanningRef.current || !videoRef.current || !mounted) return;
            try {
              const detector = new (window as any).BarcodeDetector();
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && scanningRef.current && mounted) {
                onDetected(barcodes[0].rawValue);
              }
            } catch { /* silenciar erros do detector */ }
          }, 500);
        }
      } catch {
        if (mounted) {
          setCameraError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      scanningRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (barcodeInterval) clearInterval(barcodeInterval);
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCamera]);

  // Importar código de barras a partir de um arquivo de imagem
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      const img = new Image();
      const url = URL.createObjectURL(file);

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Erro ao carregar imagem'));
        img.src = url;
      });

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Erro ao processar imagem');

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      // Tentar BarcodeDetector primeiro (suporta todos os formatos de código de barras)
      if ('BarcodeDetector' in window) {
        try {
          const bitmap = await createImageBitmap(imageData);
          const detector = new (window as any).BarcodeDetector();
          const barcodes = await detector.detect(bitmap);
          if (barcodes.length > 0) {
            if (!modoAvancado && (showNovoModelo || modeloEditando)) {
              handleBarcodePaste(barcodes[0].rawValue);
            } else {
              setTesteCodigoBarras(barcodes[0].rawValue);
            }
            utils.showNotification('Código lido com sucesso da imagem!', { type: 'success' });
            return;
          }
        } catch { /* fallback para jsQR */ }
      }

      // Fallback: jsQR (apenas QR Codes)
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code?.data) {
        if (!modoAvancado && (showNovoModelo || modeloEditando)) {
          handleBarcodePaste(code.data);
        } else {
          setTesteCodigoBarras(code.data);
        }
        utils.showNotification('QR Code lido com sucesso da imagem!', { type: 'success' });
      } else {
        utils.showNotification('Nenhum código de barras ou QR Code encontrado na imagem. Tente uma imagem com melhor qualidade.', { type: 'error' });
      }
    } catch {
      utils.showNotification('Erro ao processar a imagem. Tente novamente.', { type: 'error' });
    }
  };

  if (isLoading || !cliente) {
    return (
      <ViewLayout title="Cliente" isLoading={true} onBack={() => navigate('/clientes')}>
        <div />
      </ViewLayout>
    );
  }

  const toggleAreaAtendida = (areaId: number) => {
    setAreasAtendidas(prev =>
      prev.includes(areaId) ? prev.filter(aid => aid !== areaId) : [...prev, areaId]
    );
  };

  const selecionarTodasAreas = () => {
    if (areasAtendidas.length === todasAreas.length) {
      setAreasAtendidas([]);
    } else {
      setAreasAtendidas(todasAreas.map(a => a.id));
    }
  };

  const handleSalvarAreas = async () => {
    setSalvandoAreas(true);
    try {
      const response = await salvarAreasAtendidas(Number(id), areasAtendidas);
      if (response.success) {
        utils.showNotification(response.message || 'Áreas atendidas salvas com sucesso!', { type: 'success' });
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar áreas atendidas', { type: 'error' });
    } finally {
      setSalvandoAreas(false);
    }
  };

  return (
    <ViewLayout
      title={cliente.razao_social}
      subtitle={cliente.nome_fantasia || 'Detalhes do Cliente'}
      onBack={() => navigate('/clientes')}
    >
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '24px', borderBottom: '2px solid #e0e0e0' }}>
        <button
          onClick={() => setActiveTab('dados')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'dados' ? '#04364F' : 'transparent',
            color: activeTab === 'dados' ? '#fff' : '#7f8c8d',
            fontWeight: activeTab === 'dados' ? 'bold' : 'normal',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-building" style={{ marginRight: '8px' }}></i>
          Dados do Cliente
        </button>
        <button
          onClick={() => setActiveTab('etiquetas')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'etiquetas' ? '#04364F' : 'transparent',
            color: activeTab === 'etiquetas' ? '#fff' : '#7f8c8d',
            fontWeight: activeTab === 'etiquetas' ? 'bold' : 'normal',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-barcode" style={{ marginRight: '8px' }}></i>
          Modelos de Etiqueta ({etiquetaModelos.length})
        </button>
        <button
          onClick={() => setActiveTab('areas')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'areas' ? '#04364F' : 'transparent',
            color: activeTab === 'areas' ? '#fff' : '#7f8c8d',
            fontWeight: activeTab === 'areas' ? 'bold' : 'normal',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-map-marker-alt" style={{ marginRight: '8px' }}></i>
          Áreas Atendidas ({areasAtendidas.length})
        </button>
        <button
          onClick={() => navigate(`/clientes/${id}/usuarios`)}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            color: '#7f8c8d',
            fontWeight: 'normal',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-users" style={{ marginRight: '8px' }}></i>
          Usuários Dashboard
        </button>
        <button
          onClick={() => setActiveTab('tokens')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'tokens' ? '#04364F' : 'transparent',
            color: activeTab === 'tokens' ? '#fff' : '#7f8c8d',
            fontWeight: activeTab === 'tokens' ? 'bold' : 'normal',
            cursor: 'pointer',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px'
          }}
        >
          <i className="fas fa-key" style={{ marginRight: '8px' }}></i>
          Tokens Dashboard
        </button>
      </div>

      {/* TAB: Dados do Cliente */}
      {activeTab === 'dados' && (
        <>
          {/* Status e Ações */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span className={`status-badge ${cliente.ativo ? 'status-active' : 'status-inactive'}`}>
              {cliente.ativo ? 'Ativo' : 'Inativo'}
            </span>
            {canUpdate(SystemModule.CONFERENCIA_CARGA) && (
              <button className="btn btn-primary" onClick={() => navigate(`/clientes/editar/${id}`)}>
                <i className="fas fa-edit"></i> Editar
              </button>
            )}
          </div>

          {/* Informações Básicas */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações Básicas
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label>Razão Social</label>
                <span>{cliente.razao_social}</span>
              </div>
              <div className="info-item">
                <label>Nome Fantasia</label>
                <span>{cliente.nome_fantasia || '-'}</span>
              </div>
              <div className="info-item">
                <label>CNPJ</label>
                <span>{formatCNPJ(cliente.cnpj)}</span>
              </div>
              <div className="info-item">
                <label>Inscrição Estadual</label>
                <span>{cliente.inscricao_estadual || '-'}</span>
              </div>
              <div className="info-item">
                <label>Inscrição Municipal</label>
                <span>{cliente.inscricao_municipal || '-'}</span>
              </div>
            </div>
          </div>

          {/* Contato */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Contato
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item">
                <label>Email</label>
                <span>{cliente.email || '-'}</span>
              </div>
              <div className="info-item">
                <label>Telefone</label>
                <span>{cliente.telefone || '-'}</span>
              </div>
              <div className="info-item">
                <label>Celular</label>
                <span>{cliente.celular || '-'}</span>
              </div>
              <div className="info-item">
                <label>Contato</label>
                <span>{cliente.contato_nome ? `${cliente.contato_nome}${cliente.contato_cargo ? ` (${cliente.contato_cargo})` : ''}` : '-'}</span>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Endereço
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="info-item" style={{ gridColumn: 'span 2' }}>
                <label>Endereço Completo</label>
                <span>
                  {cliente.endereco_rua
                    ? `${cliente.endereco_rua}${cliente.endereco_numero ? ', ' + cliente.endereco_numero : ''}${cliente.endereco_complemento ? ' - ' + cliente.endereco_complemento : ''}`
                    : '-'}
                </span>
              </div>
              <div className="info-item">
                <label>Bairro</label>
                <span>{cliente.endereco_bairro || '-'}</span>
              </div>
              <div className="info-item">
                <label>Cidade/UF</label>
                <span>{cliente.endereco_cidade ? `${cliente.endereco_cidade}/${cliente.endereco_uf || ''}` : '-'}</span>
              </div>
              <div className="info-item">
                <label>CEP</label>
                <span>{cliente.endereco_cep || '-'}</span>
              </div>
            </div>
          </div>

          {/* Observações */}
          {cliente.observacoes && (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                Observações
              </h3>
              <p style={{ color: '#555', lineHeight: 1.6 }}>{cliente.observacoes}</p>
            </div>
          )}
        </>
      )}

      {/* TAB: Modelos de Etiqueta */}
      {/* TAB: Modelos de Etiqueta */}
      {activeTab === 'etiquetas' && (
        <>
          {/* Informação */}
          <div style={{
            background: '#f0f9ff', border: '1px solid #bae6fd',
            borderRadius: '8px', padding: '16px', marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <i className="fas fa-info-circle" style={{ color: '#0284c7', marginTop: '2px' }}></i>
              <div>
                <strong style={{ color: '#0c4a6e' }}>Modelos de Etiqueta</strong>
                <p style={{ color: '#475569', margin: '4px 0 0', fontSize: '13px' }}>
                  Cadastre diferentes modelos de etiqueta para este cliente. Cada modelo define um padrão diferente
                  de código de barras. Na conferência, o sistema tenta identificar automaticamente qual modelo corresponde à etiqueta lida.
                </p>
              </div>
            </div>
          </div>

          {/* Botão Novo Modelo */}
          {canUpdate(SystemModule.CONFERENCIA_CARGA) && !modeloEditando && !showNovoModelo && (
            <div style={{ marginBottom: '24px' }}>
              <button type="button" className="btn btn-primary" onClick={iniciarNovoModelo}>
                <i className="fas fa-plus"></i> Novo Modelo de Etiqueta
              </button>
            </div>
          )}

          {/* Formulário de Edição/Criação de Modelo */}
          {(modeloEditando || showNovoModelo) && (
            <div style={{
              background: '#fff', border: '2px solid #04364F',
              borderRadius: '12px', padding: '24px', marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h4 style={{ margin: 0, color: '#04364F' }}>
                  <i className="fas fa-tag" style={{ marginRight: '8px' }}></i>
                  {modeloEditando ? `Editar: ${modeloEditando.nome}` : 'Novo Modelo de Etiqueta'}
                </h4>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button type="button" onClick={() => {
                      if (modoAvancado && modeloConfigs.length > 0) {
                        if (!window.confirm('Ao trocar para o modo visual, os campos regex atuais não serão preservados. Deseja continuar?')) return;
                      }
                      setModoAvancado(!modoAvancado);
                    }}
                    style={{
                      background: modoAvancado ? '#6366f1' : '#f1f5f9',
                      color: modoAvancado ? '#fff' : '#64748b',
                      border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px'
                    }}>
                    <i className="fas fa-code" style={{ marginRight: '4px' }}></i>
                    {modoAvancado ? 'Modo Avançado (Regex)' : 'Modo Avançado'}
                  </button>
                  <button type="button" onClick={cancelarEdicaoModelo}
                    style={{ background: '#f1f5f9', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', color: '#64748b', fontSize: '14px' }}>
                    <i className="fas fa-times"></i> Cancelar
                  </button>
                </div>
              </div>

              {/* Nome e Descrição */}
              <div className="info-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Nome do Modelo *</label>
                  <input type="text" value={modeloNome} onChange={(e) => setModeloNome(e.target.value)}
                    className="form-control" placeholder="Ex: Etiqueta Padrão, Etiqueta Caixa..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Descrição</label>
                  <input type="text" value={modeloDescricao} onChange={(e) => setModeloDescricao(e.target.value)}
                    className="form-control" placeholder="Descrição do modelo (opcional)" />
                </div>
              </div>

              {/* ====== MODO VISUAL ====== */}
              {!modoAvancado && (
                <>
                  {/* Passo 1: Código de barras de exemplo */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '4px', color: '#334155', fontSize: '14px' }}>
                      <i className="fas fa-barcode" style={{ marginRight: '8px', color: '#04364F' }}></i>
                      Passo 1: Cole um código de barras de exemplo
                    </h4>
                    <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 12px' }}>
                      Cole um código de barras real da etiqueta deste cliente. O sistema irá dividir automaticamente em colunas.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input type="text" value={codigoBarrasExemplo} onChange={(e) => handleBarcodePaste(e.target.value)}
                          className="form-control" placeholder="Ex: 3175000648-0002-0004-47277519000185-33300"
                          style={{ fontFamily: 'monospace', fontSize: '14px', paddingRight: '80px' }} />
                        <div style={{ position: 'absolute', right: '4px', bottom: '4px', display: 'flex', gap: '2px' }}>
                          <button type="button" onClick={() => fileInputRef.current?.click()} title="Importar de imagem"
                            style={{ background: 'none', border: 'none', color: '#7f8c8d', fontSize: '1.1rem', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px' }}>
                            <i className="fas fa-file-image"></i>
                          </button>
                          <button type="button" onClick={() => { setCameraError(null); setShowCamera(true); }} title="Escanear com câmera"
                            style={{ background: 'none', border: 'none', color: '#7f8c8d', fontSize: '1.1rem', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px' }}>
                            <i className="fas fa-camera"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Passo 2: Separador + Colunas */}
                  {codigoBarrasExemplo.trim() && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                      <h4 style={{ marginBottom: '4px', color: '#334155', fontSize: '14px' }}>
                        <i className="fas fa-columns" style={{ marginRight: '8px', color: '#04364F' }}></i>
                        Passo 2: Separador e mapeamento de colunas
                      </h4>
                      <p style={{ color: '#64748b', fontSize: '12px', margin: '0 0 16px' }}>
                        O separador foi detectado automaticamente. Ajuste se necessário e defina o significado de cada coluna.
                      </p>

                      {/* Seletor de separador */}
                      <div style={{ marginBottom: '16px' }}>
                        <label className="form-label" style={{ fontSize: '12px', marginBottom: '6px' }}>Separador</label>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {SEPARADORES_COMUNS.map(sep => (
                            <button key={sep.valor} type="button" onClick={() => handleSeparadorChange(sep.valor)}
                              style={{
                                padding: '6px 14px', borderRadius: '6px',
                                border: separador === sep.valor ? '2px solid #04364F' : '1px solid #e2e8f0',
                                background: separador === sep.valor ? '#04364F' : '#fff',
                                color: separador === sep.valor ? '#fff' : '#334155',
                                cursor: 'pointer', fontSize: '13px', fontWeight: separador === sep.valor ? 600 : 400
                              }}>
                              {sep.label}
                            </button>
                          ))}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>Outro:</span>
                            <input type="text" value={separadorCustom}
                              onChange={(e) => { setSeparadorCustom(e.target.value); if (e.target.value.length === 1) handleSeparadorChange(e.target.value); }}
                              style={{ width: '50px', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontFamily: 'monospace', fontSize: '14px', textAlign: 'center' }}
                              maxLength={1} placeholder="?" />
                          </div>
                        </div>
                      </div>

                      {/* Colunas visuais */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '16px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                        {colunaMapeamentos.map((col, idx) => {
                          const cores = TIPO_CORES[col.tipo];
                          return (
                            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '110px', flex: '0 1 auto' }}>
                              <div style={{
                                background: cores.bg, border: `2px solid ${cores.border}`, borderRadius: '8px',
                                padding: '8px 12px', width: '100%', textAlign: 'center', fontFamily: 'monospace',
                                fontSize: '13px', fontWeight: 600, color: cores.text, wordBreak: 'break-all',
                                minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}>
                                {col.valor}
                              </div>
                              <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600 }}>Coluna {idx + 1}</span>
                              <select value={col.tipo} onChange={(e) => updateColunaTipo(idx, e.target.value as ColunaTipo)}
                                style={{
                                  width: '100%', padding: '4px 6px', fontSize: '11px', borderRadius: '4px',
                                  border: '1px solid #e2e8f0', background: col.tipo !== 'ignorar' ? cores.bg : '#fff',
                                  color: cores.text, fontWeight: col.tipo !== 'ignorar' ? 600 : 400, cursor: 'pointer'
                                }}>
                                <option value="ignorar">-- Ignorar --</option>
                                <option value="cnpj">CNPJ Destinatário</option>
                                <option value="area">Cód. Área Entrega</option>
                                <option value="pedido">N. do Pedido</option>
                                <option value="volume_atual">Volume Atual</option>
                                <option value="total_volumes">Total de Volumes</option>
                                <option value="personalizado">Personalizado...</option>
                              </select>
                              {col.tipo === 'personalizado' && (
                                <input type="text" value={col.nomePersonalizado} onChange={(e) => updateColunaNome(idx, e.target.value)}
                                  placeholder="Nome..." style={{ width: '100%', padding: '3px 6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #86efac', background: '#f0fdf4' }} />
                              )}
                              {col.tipo !== 'ignorar' && (
                                <span style={{ fontSize: '10px', color: cores.text, background: cores.bg, padding: '2px 6px', borderRadius: '10px', border: `1px solid ${cores.border}` }}>
                                  <i className={TIPO_ICONS[col.tipo]} style={{ fontSize: '9px', marginRight: '3px' }}></i>
                                  {col.tipo === 'personalizado' ? (col.nomePersonalizado || 'Custom') : TIPO_LABELS[col.tipo]}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Preview dos valores extraídos */}
                      {colunaMapeamentos.some(c => c.tipo !== 'ignorar') && (
                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '14px' }}>
                          <h5 style={{ margin: '0 0 10px', color: '#166534', fontSize: '13px' }}>
                            <i className="fas fa-check-circle" style={{ marginRight: '6px' }}></i>
                            Preview dos valores extraídos
                          </h5>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {colunaMapeamentos.filter(c => c.tipo !== 'ignorar').map((col, i) => {
                              const label = col.tipo === 'personalizado' ? (col.nomePersonalizado || 'Personalizado') : TIPO_LABELS[col.tipo as Exclude<ColunaTipo, 'ignorar'>];
                              const cores = TIPO_CORES[col.tipo];
                              return (
                                <div key={i} style={{ background: '#fff', border: `1px solid ${cores.border}`, borderRadius: '6px', padding: '8px 12px', fontSize: '12px' }}>
                                  <span style={{ color: '#64748b' }}>{label}: </span>
                                  <strong style={{ color: cores.text, fontFamily: 'monospace' }}>{col.valor}</strong>
                                </div>
                              );
                            })}
                          </div>
                          {(() => {
                            const hasPedido = colunaMapeamentos.some(c => c.tipo === 'pedido');
                            const hasVolAtual = colunaMapeamentos.some(c => c.tipo === 'volume_atual');
                            const hasVolTotal = colunaMapeamentos.some(c => c.tipo === 'total_volumes');
                            const volCount = [hasPedido, hasVolAtual, hasVolTotal].filter(Boolean).length;
                            if (volCount > 0 && volCount < 3) {
                              return (
                                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fffbeb', borderRadius: '6px', borderLeft: '3px solid #f59e0b', fontSize: '12px', color: '#92400e' }}>
                                  <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                                  Para rastreamento de volumes, mapeie os 3 campos: N. do Pedido, Volume Atual e Total de Volumes.
                                </div>
                              );
                            }
                            if (volCount === 3) {
                              return (
                                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#f0fdf4', borderRadius: '6px', borderLeft: '3px solid #16a34a', fontSize: '12px', color: '#166534' }}>
                                  <i className="fas fa-check-circle" style={{ marginRight: '6px' }}></i>
                                  Rastreamento de volumes configurado.
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Testador visual */}
                  {colunaMapeamentos.some(c => c.tipo !== 'ignorar') && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                      <h4 style={{ marginBottom: '12px', color: '#334155', fontSize: '14px' }}>
                        <i className="fas fa-flask" style={{ marginRight: '8px' }}></i>
                        Testar com outro código de barras
                      </h4>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <input type="text" value={testeCodigoBarras} onChange={(e) => setTesteCodigoBarras(e.target.value)}
                            className="form-control" placeholder="Cole outro código de barras para validar..." style={{ fontFamily: 'monospace' }} />
                        </div>
                        <button type="button" className="btn btn-primary" onClick={handleTestarTodosRegex}
                          disabled={!testeCodigoBarras.trim() || !colunaMapeamentos.some(c => c.tipo !== 'ignorar')} style={{ whiteSpace: 'nowrap' }}>
                          <i className="fas fa-play"></i> Testar
                        </button>
                      </div>
                      {Object.keys(testeResultados).length > 0 && (() => {
                        const visualConfigs = buildConfigsFromVisual().configs;
                        return (
                        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {Object.entries(testeResultados).map(([idx, res]) => {
                            const config = visualConfigs[Number(idx)];
                            return (
                              <div key={idx} style={{
                                padding: '8px 12px', borderRadius: '6px', fontSize: '12px',
                                background: res.match ? '#f0fdf4' : '#fef2f2',
                                border: `1px solid ${res.match ? '#bbf7d0' : '#fecaca'}`
                              }}>
                                <i className={`fas ${res.match ? 'fa-check-circle' : 'fa-times-circle'}`}
                                  style={{ color: res.match ? '#16a34a' : '#dc2626', marginRight: '4px' }}></i>
                                <strong>{config?.nome_campo || `Campo ${Number(idx) + 1}`}: </strong>
                                {res.resultado ? <span style={{ fontFamily: 'monospace' }}>{res.resultado}</span> : <span style={{ color: '#dc2626' }}>sem match</span>}
                              </div>
                            );
                          })}
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
              {/* ====== MODO AVANÇADO (REGEX) ====== */}
              {modoAvancado && (
                <>
                  {/* Guia de Regex (colapsável) */}
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '20px', overflow: 'hidden' }}>
                    <button type="button" onClick={() => setShowRegexGuide(prev => !prev)}
                      style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#334155' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        <i className="fas fa-book" style={{ marginRight: '8px', color: '#6366f1' }}></i>
                        Como funciona o Comando Regex?
                      </span>
                      <i className={`fas fa-chevron-${showRegexGuide ? 'up' : 'down'}`} style={{ color: '#94a3b8' }}></i>
                    </button>
                    {showRegexGuide && (
                      <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e2e8f0' }}>
                        <h5 style={{ margin: '16px 0 10px', color: '#334155', fontSize: '13px' }}>
                          <i className="fas fa-code" style={{ marginRight: '6px', color: '#6366f1' }}></i>
                          Sintaxe Básica
                        </h5>
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '16px' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Padrão</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Significado</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Exemplo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                ['( )', 'Grupo de captura — extrai conteúdo dentro dos parênteses', '^(\\d+) → captura números no início'],
                                ['.', 'Qualquer caractere', '.{5} → 5 caracteres quaisquer'],
                                ['\\d', 'Qualquer dígito (0-9)', '\\d{14} → 14 dígitos (CNPJ)'],
                                ['\\w', 'Letra, dígito ou underscore', '\\w+ → uma ou mais letras/dígitos'],
                                ['+', 'Um ou mais do anterior', '\\d+ → um ou mais dígitos'],
                                ['{n}', 'Exatamente n repetições', '.{20} → 20 caracteres'],
                                ['^', 'Início da string', '^(\\d+) → dígitos do início'],
                                ['$', 'Final da string', '(\\d+)$ → dígitos do final'],
                                ['[^X]+', 'Qualquer coisa exceto X', '[^-]+ → tudo exceto hífen'],
                              ].map(([pattern, meaning, example], i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '6px 12px', fontFamily: 'monospace', color: '#6366f1', fontWeight: 'bold' }}>{pattern}</td>
                                  <td style={{ padding: '6px 12px', color: '#475569' }}>{meaning}</td>
                                  <td style={{ padding: '6px 12px', fontFamily: 'monospace', color: '#64748b', fontSize: '11px' }}>{example}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                          <h5 style={{ margin: '0 0 8px', color: '#92400e', fontSize: '13px' }}>
                            <i className="fas fa-lightbulb" style={{ marginRight: '6px' }}></i>Exemplo Prático
                          </h5>
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#78716c' }}>
                            Código: <code style={{ background: '#fff7ed', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold' }}>3175000648-0002-0004-47277519000185-33300</code>
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {[
                              ['Pedido', '^([^-]+)', '3175000648'],
                              ['Volume Atual', '^(?:[^-]+-){1}([^-]+)', '0002'],
                              ['Total Volumes', '^(?:[^-]+-){2}([^-]+)', '0004'],
                              ['CNPJ', '^(?:[^-]+-){3}([^-]+)', '47277519000185'],
                            ].map(([field, regex, result], i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                <span style={{ color: '#334155', fontWeight: 'bold', minWidth: '120px' }}>{field}:</span>
                                <code style={{ background: '#dbeafe', padding: '2px 6px', borderRadius: '3px', color: '#1e40af' }}>{regex}</code>
                                <span style={{ color: '#64748b' }}>→</span>
                                <code style={{ background: '#f0fdf4', padding: '2px 6px', borderRadius: '3px', color: '#166534', fontWeight: 'bold' }}>{result}</code>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '14px' }}>
                          <h5 style={{ margin: '0 0 6px', color: '#1e40af', fontSize: '13px' }}>
                            <i className="fas fa-magic" style={{ marginRight: '6px' }}></i>Valor Padrão (sem regex)
                          </h5>
                          <p style={{ margin: 0, color: '#3b82f6', fontSize: '12px' }}>
                            Use <code style={{ background: '#dbeafe', padding: '2px 6px', borderRadius: '3px' }}>VALOR_PADRAO=seu_valor</code> para definir um valor fixo.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Campos de Identificação Padrão */}
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '4px', color: '#1e40af', fontSize: '14px' }}>
                      <i className="fas fa-id-card" style={{ marginRight: '8px' }}></i>Campos de Identificação Padrão
                    </h4>
                    <p style={{ color: '#3b82f6', fontSize: '12px', margin: '0 0 16px' }}>
                      Selecione quais campos representam o CNPJ do destinatário e o código da área de entrega.
                    </p>
                    <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>
                          <i className="fas fa-building" style={{ marginRight: '4px', color: '#2563eb' }}></i>CNPJ do Destinatário
                        </label>
                        <select value={modeloCampoCnpj} onChange={(e) => setModeloCampoCnpj(e.target.value)} className="form-control" style={{ fontSize: '13px' }}>
                          <option value="">-- Nenhum --</option>
                          {modeloConfigs.filter(c => c.nome_campo.trim()).map((c, i) => (<option key={i} value={c.nome_campo}>{c.nome_campo}</option>))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>
                          <i className="fas fa-map-marker-alt" style={{ marginRight: '4px', color: '#2563eb' }}></i>Código Área de Entrega
                        </label>
                        <select value={modeloCampoCodigoSetor} onChange={(e) => setModeloCampoCodigoSetor(e.target.value)} className="form-control" style={{ fontSize: '13px' }}>
                          <option value="">-- Nenhum --</option>
                          {modeloConfigs.filter(c => c.nome_campo.trim()).map((c, i) => (<option key={i} value={c.nome_campo}>{c.nome_campo}</option>))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Mapeamento de Volumes */}
                  <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '4px', color: '#92400e', fontSize: '14px' }}>
                      <i className="fas fa-boxes" style={{ marginRight: '8px' }}></i>Rastreamento de Volumes (Opcional)
                    </h4>
                    <p style={{ color: '#a16207', fontSize: '12px', margin: '0 0 16px' }}>
                      Selecione os campos de pedido, volume atual e total para ativar o rastreamento.
                    </p>
                    <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>
                          <i className="fas fa-hashtag" style={{ marginRight: '4px', color: '#d97706' }}></i>N. do Pedido
                        </label>
                        <select value={modeloCampoNumeroPedido} onChange={(e) => setModeloCampoNumeroPedido(e.target.value)} className="form-control" style={{ fontSize: '13px' }}>
                          <option value="">-- Nenhum --</option>
                          {modeloConfigs.filter(c => c.nome_campo.trim()).map((c, i) => (
                            <option key={i} value={c.nome_campo} disabled={c.nome_campo === modeloCampoVolumeAtual || c.nome_campo === modeloCampoTotalVolumes}>{c.nome_campo}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>
                          <i className="fas fa-sort-numeric-up" style={{ marginRight: '4px', color: '#d97706' }}></i>Volume Atual
                        </label>
                        <select value={modeloCampoVolumeAtual} onChange={(e) => setModeloCampoVolumeAtual(e.target.value)} className="form-control" style={{ fontSize: '13px' }}>
                          <option value="">-- Nenhum --</option>
                          {modeloConfigs.filter(c => c.nome_campo.trim()).map((c, i) => (
                            <option key={i} value={c.nome_campo} disabled={c.nome_campo === modeloCampoNumeroPedido || c.nome_campo === modeloCampoTotalVolumes}>{c.nome_campo}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: '12px' }}>
                          <i className="fas fa-layer-group" style={{ marginRight: '4px', color: '#d97706' }}></i>Total de Volumes
                        </label>
                        <select value={modeloCampoTotalVolumes} onChange={(e) => setModeloCampoTotalVolumes(e.target.value)} className="form-control" style={{ fontSize: '13px' }}>
                          <option value="">-- Nenhum --</option>
                          {modeloConfigs.filter(c => c.nome_campo.trim()).map((c, i) => (
                            <option key={i} value={c.nome_campo} disabled={c.nome_campo === modeloCampoNumeroPedido || c.nome_campo === modeloCampoVolumeAtual}>{c.nome_campo}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Testador de Regex */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '12px', color: '#334155', fontSize: '14px' }}>
                      <i className="fas fa-flask" style={{ marginRight: '8px' }}></i>Testar Código de Barras
                    </h4>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <label className="form-label">Código de Barras para Teste</label>
                        <input type="text" value={testeCodigoBarras} onChange={(e) => setTesteCodigoBarras(e.target.value)}
                          className="form-control" placeholder="Cole aqui o código de barras..." style={{ fontFamily: 'monospace', paddingRight: '80px' }} />
                        <div style={{ position: 'absolute', right: '4px', bottom: '4px', display: 'flex', gap: '2px' }}>
                          <button type="button" onClick={() => fileInputRef.current?.click()} title="Importar de imagem"
                            style={{ background: 'none', border: 'none', color: '#7f8c8d', fontSize: '1.1rem', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px' }}>
                            <i className="fas fa-file-image"></i>
                          </button>
                          <button type="button" onClick={() => { setCameraError(null); setShowCamera(true); }} title="Escanear com câmera"
                            style={{ background: 'none', border: 'none', color: '#7f8c8d', fontSize: '1.1rem', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px' }}>
                            <i className="fas fa-camera"></i>
                          </button>
                        </div>
                      </div>
                      <button type="button" className="btn btn-primary" onClick={handleTestarTodosRegex}
                        disabled={!testeCodigoBarras.trim() || modeloConfigs.length === 0} style={{ whiteSpace: 'nowrap' }}>
                        <i className="fas fa-play"></i> Testar Todos
                      </button>
                    </div>
                  </div>
                  {/* Lista de Campos do Modelo */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h5 style={{ margin: 0, color: '#334155', fontSize: '14px' }}>
                        <i className="fas fa-list" style={{ marginRight: '6px' }}></i>Campos ({modeloConfigs.length})
                      </h5>
                      <button type="button" onClick={addModeloConfig}
                        style={{ background: '#04364F', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}>
                        <i className="fas fa-plus" style={{ marginRight: '4px' }}></i> Adicionar Campo
                      </button>
                    </div>
                    {modeloConfigs.length === 0 ? (
                      <div style={{ padding: '30px 20px', textAlign: 'center', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                        <i className="fas fa-barcode" style={{ fontSize: '2rem', color: '#cbd5e1', marginBottom: '8px', display: 'block' }}></i>
                        <p style={{ color: '#94a3b8', margin: 0 }}>Nenhum campo adicionado. Clique em "Adicionar Campo" para começar.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {modeloConfigs.map((config, index) => (
                          <div key={index} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ background: '#04364F', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                                  {index + 1}
                                </span>
                                <strong style={{ color: '#334155', fontSize: '13px' }}>{config.nome_campo || `Campo ${index + 1}`}</strong>
                              </div>
                              <button type="button" onClick={() => removeModeloConfig(index)}
                                style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                              <div className="form-group">
                                <label className="form-label" style={{ fontSize: '12px' }}>Variável *</label>
                                <input type="text" value={config.nome_campo} onChange={(e) => updateModeloConfig(index, 'nome_campo', e.target.value)}
                                  className="form-control" placeholder="Ex: Total Volume..." style={{ fontSize: '13px' }} />
                              </div>
                              <div className="form-group">
                                <label className="form-label" style={{ fontSize: '12px' }}>Descrição</label>
                                <input type="text" value={config.descricao || ''} onChange={(e) => updateModeloConfig(index, 'descricao', e.target.value)}
                                  className="form-control" placeholder="Descrição" style={{ fontSize: '13px' }} />
                              </div>
                              <div className="form-group">
                                <label className="form-label" style={{ fontSize: '12px' }}>Tipo</label>
                                <select value={config.tipo_dado} onChange={(e) => updateModeloConfig(index, 'tipo_dado', e.target.value)}
                                  className="form-control" style={{ fontSize: '13px' }}>
                                  <option value="texto">Texto</option>
                                  <option value="numero">Número</option>
                                  <option value="data">Data</option>
                                  <option value="cnpj">CNPJ</option>
                                  <option value="cpf">CPF</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label className="form-label" style={{ fontSize: '12px' }}>Obrigatório</label>
                                <select value={config.obrigatorio ? 'true' : 'false'} onChange={(e) => updateModeloConfig(index, 'obrigatorio', e.target.value === 'true')}
                                  className="form-control" style={{ fontSize: '13px' }}>
                                  <option value="false">Não</option>
                                  <option value="true">Sim</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                              <div className="form-group">
                                <label className="form-label" style={{ fontSize: '12px' }}>
                                  Comando Regex * <span style={{ color: '#94a3b8', fontSize: '11px' }}>(padrão para extrair dados)</span>
                                </label>
                                <input type="text" value={config.regex_pattern} onChange={(e) => updateModeloConfig(index, 'regex_pattern', e.target.value)}
                                  className="form-control" placeholder="Ex: ^(\d{14}) ou .{20}(.{10})" style={{ fontFamily: 'monospace', fontSize: '13px' }} />
                              </div>
                            </div>
                            {testeResultados[index] && (
                              <div style={{
                                marginTop: '10px', padding: '10px', borderRadius: '6px',
                                background: testeResultados[index].match ? '#f0fdf4' : '#fef2f2',
                                border: `1px solid ${testeResultados[index].match ? '#bbf7d0' : '#fecaca'}`
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <i className={`fas ${testeResultados[index].match ? 'fa-check-circle' : 'fa-times-circle'}`}
                                    style={{ color: testeResultados[index].match ? '#16a34a' : '#dc2626' }}></i>
                                  <strong style={{ color: testeResultados[index].match ? '#166534' : '#991b1b', fontSize: '13px' }}>
                                    {testeResultados[index].match ? 'Match encontrado' : 'Sem match'}
                                  </strong>
                                </div>
                                {testeResultados[index].resultado && (
                                  <div style={{ marginTop: '6px', fontFamily: 'monospace', fontSize: '13px', color: '#334155' }}>
                                    Resultado: <strong>{testeResultados[index].resultado}</strong>
                                  </div>
                                )}
                                {testeResultados[index].erro && (
                                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>{testeResultados[index].erro}</div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
              {/* Botão Salvar Modelo */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={cancelarEdicaoModelo}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={handleSalvarModelo}
                  disabled={isSavingEtiquetas || !modeloNome.trim()}>
                  {isSavingEtiquetas ? (
                    <><span className="loading"></span> Salvando...</>
                  ) : (
                    <><i className="fas fa-save"></i> {modeloEditando ? 'Salvar Alterações' : 'Criar Modelo'}</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Lista de Modelos Existentes */}
          {!modeloEditando && !showNovoModelo && (
            <div style={{ marginBottom: '20px' }}>
              {etiquetaModelos.length === 0 ? (
                <div className="empty-state" style={{ padding: '40px 20px' }}>
                  <i className="fas fa-tags empty-state-icon"></i>
                  <h3>Nenhum modelo de etiqueta cadastrado</h3>
                  <p>Crie modelos de etiqueta para configurar os diferentes padrões de código de barras deste cliente.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {etiquetaModelos.map((modelo, mIdx) => (
                    <div
                      key={modelo.id || mIdx}
                      style={{
                        background: '#fff', border: '1px solid #e2e8f0',
                        borderRadius: '12px', padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px', color: '#04364F', fontSize: '16px' }}>
                            <i className="fas fa-tag" style={{ marginRight: '8px', color: '#04364F' }}></i>
                            {modelo.nome}
                          </h4>
                          {modelo.descricao && (
                            <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>{modelo.descricao}</p>
                          )}
                        </div>
                        {canUpdate(SystemModule.CONFERENCIA_CARGA) && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => iniciarEdicaoModelo(modelo)}
                              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}>
                              <i className="fas fa-edit" style={{ marginRight: '4px' }}></i>Editar
                            </button>
                            <button type="button"
                              onClick={() => handleDeletarModelo(modelo)}
                              style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px' }}>
                              <i className="fas fa-trash" style={{ marginRight: '4px' }}></i>Excluir
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Info badges */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', color: '#475569' }}>
                          <i className="fas fa-list" style={{ marginRight: '4px' }}></i>{modelo.configs.length} campo(s)
                        </span>
                        {modelo.campo_cnpj && (
                          <span style={{ background: '#dbeafe', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', color: '#1e40af' }}>
                            <i className="fas fa-building" style={{ marginRight: '4px' }}></i>CNPJ: {modelo.campo_cnpj}
                          </span>
                        )}
                        {modelo.campo_codigo_setor && (
                          <span style={{ background: '#dbeafe', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', color: '#1e40af' }}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>Área: {modelo.campo_codigo_setor}
                          </span>
                        )}
                        {modelo.campo_numero_pedido && modelo.campo_volume_atual && modelo.campo_total_volumes && (
                          <span style={{ background: '#fef3c7', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', color: '#92400e' }}>
                            <i className="fas fa-boxes" style={{ marginRight: '4px' }}></i>Volumes rastreados
                          </span>
                        )}
                      </div>

                      {/* Campos do modelo (colapsável) */}
                      {modelo.configs.length > 0 && (
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {modelo.configs.map((cfg, cIdx) => (
                              <div key={cIdx} style={{
                                background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px',
                                padding: '8px 12px', fontSize: '12px'
                              }}>
                                <strong style={{ color: '#334155' }}>{cfg.nome_campo}</strong>
                                <div style={{ fontFamily: 'monospace', color: '#6366f1', fontSize: '11px', marginTop: '2px' }}>
                                  {cfg.regex_pattern}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {/* Input de arquivo oculto para importar imagem */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Modal da câmera para escanear código de barras */}
      {showCamera && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '520px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, color: '#334155' }}>
                <i className="fas fa-camera" style={{ marginRight: '8px', color: '#04364F' }}></i>
                Escanear Código
              </h4>
              <button
                onClick={() => setShowCamera(false)}
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '6px 10px'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4/3',
              background: '#000',
              borderRadius: '8px',
              overflow: 'hidden',
              marginBottom: '16px'
            }}>
              <video
                ref={videoRef}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                playsInline
                muted
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              {/* Overlay de escaneamento */}
              {!cameraError && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '220px',
                  height: '220px',
                  border: '3px solid #04C64F',
                  borderRadius: '12px',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'
                }} />
              )}
            </div>

            {cameraError ? (
              <div style={{
                padding: '12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                {cameraError}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', margin: 0 }}>
                <i className="fas fa-crosshairs" style={{ marginRight: '6px' }}></i>
                Aponte a câmera para o QR Code ou código de barras da etiqueta
              </p>
            )}
          </div>
        </div>
      )}
      {/* TAB: Áreas Atendidas */}
      {activeTab === 'areas' && (
        <div>
          <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: 16 }}>
            Selecione as áreas de entrega que este cliente atende. Ao montar ou agendar uma carga para este cliente,
            apenas as áreas selecionadas serão exibidas. Se nenhuma área for selecionada, todas as áreas serão exibidas.
          </p>

          {todasAreas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <i className="fas fa-map-marker-alt" style={{ fontSize: '2.5rem', color: '#ddd', display: 'block', marginBottom: 12 }}></i>
              <p>Nenhuma área de entrega cadastrada no sistema</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2c3e50' }}>
                  {areasAtendidas.length} de {todasAreas.length} áreas selecionadas
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={selecionarTodasAreas}
                  style={{ fontSize: '0.75rem' }}
                >
                  {areasAtendidas.length === todasAreas.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 350, overflowY: 'auto', padding: 4, marginBottom: 16 }}>
                {todasAreas.map(area => (
                  <label
                    key={area.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                      borderRadius: 8, border: `2px solid ${areasAtendidas.includes(area.id) ? '#04C64F' : '#dce1e6'}`,
                      background: areasAtendidas.includes(area.id) ? '#e8f9ef' : 'white',
                      cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s',
                      userSelect: 'none'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={areasAtendidas.includes(area.id)}
                      onChange={() => toggleAreaAtendida(area.id)}
                      style={{ accentColor: '#04C64F' }}
                    />
                    <span>
                      {area.nome}
                      {area.regiao && <span style={{ color: '#7f8c8d' }}> ({area.regiao})</span>}
                    </span>
                  </label>
                ))}
              </div>

              {canUpdate(SystemModule.CONFERENCIA_CARGA) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSalvarAreas}
                    disabled={salvandoAreas}
                  >
                    {salvandoAreas ? (
                      <><span className="loading"></span> Salvando...</>
                    ) : (
                      <><i className="fas fa-save" style={{ marginRight: 6 }}></i> Salvar Áreas Atendidas</>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* TAB: Tokens Dashboard */}
      {activeTab === 'tokens' && (
        <div>
          <div style={{
            background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
            padding: 16, marginBottom: 24, fontSize: 13, color: '#0c4a6e'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
            Gere tokens para que este cliente possa acessar o Dashboard Consolidado e visualizar os dados da sua transportadora.
          </div>

          <div style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            padding: 24, marginBottom: 24
          }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#1e293b' }}>Gerar Novo Token</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <input
                type="text"
                value={dashTokenDescricao}
                onChange={e => setDashTokenDescricao(e.target.value)}
                placeholder={`Descrição do token (ex: Dashboard ${cliente?.nome_fantasia || ''})`}
                style={{
                  flex: 1, padding: '10px 14px', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14
                }}
              />
              <button
                onClick={handleCreateDashToken}
                disabled={dashTokenCreating}
                style={{
                  background: '#04364F', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  fontWeight: 500, opacity: dashTokenCreating ? 0.7 : 1
                }}
              >
                <i className="fas fa-plus" style={{ fontSize: 12 }}></i>
                {dashTokenCreating ? 'Gerando...' : 'Gerar Token'}
              </button>
            </div>
          </div>

          {dashTokensLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Carregando...</div>
          ) : dashTokens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
              <i className="fas fa-key" style={{ fontSize: 24, marginBottom: 12, display: 'block' }}></i>
              Nenhum token gerado para este cliente. Crie um token acima para compartilhar.
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Descrição</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Token</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Criado em</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {dashTokens.map(tk => (
                    <tr key={tk.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px', fontSize: 14 }}>{tk.descricao || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{
                          background: '#f1f5f9', padding: '4px 8px', borderRadius: 4,
                          fontSize: 12, color: '#475569', fontFamily: 'monospace',
                          maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}>
                          {tk.token.substring(0, 16)}...
                        </code>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                        {new Date(tk.criado_em).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => copyDashToken(tk.id, tk.token)}
                            title="Copiar token"
                            style={{
                              padding: '6px 12px', background: dashTokenCopiedId === tk.id ? '#dcfce7' : '#eff6ff',
                              border: `1px solid ${dashTokenCopiedId === tk.id ? '#bbf7d0' : '#bfdbfe'}`,
                              borderRadius: 6, cursor: 'pointer',
                              color: dashTokenCopiedId === tk.id ? '#15803d' : '#2563eb',
                              fontSize: 12, display: 'flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <i className="fas fa-copy" style={{ fontSize: 11 }}></i>
                            {dashTokenCopiedId === tk.id ? 'Copiado!' : 'Copiar'}
                          </button>
                          <button
                            onClick={() => handleDeleteDashToken(tk.id)}
                            title="Excluir"
                            style={{
                              padding: 6, background: '#fef2f2', border: '1px solid #fecaca',
                              borderRadius: 6, cursor: 'pointer', color: '#b91c1c'
                            }}
                          >
                            <i className="fas fa-trash" style={{ fontSize: 12 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Marketplace de Integrações */}
      {id && (
        <div style={{ marginTop: '30px' }}>
          <IntegracaoMarketplace clienteId={Number(id)} />
        </div>
      )}
    </ViewLayout>
  );
};

export default ClienteView;
